import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { api, useAuth } from '@shop/api-client'
import { Perm } from '../api/types'
import type { Revaluation, UncostedItem } from '../api/types'
import { formatMoney, formatQty, toNumber } from '../ui/format'
import {
  Banner, Button, Empty, ErrorBanner, Field, NumberInput, Panel, Spinner, TextInput,
} from '@shop/ui'

/**
 * ما على الرفّ بلا كلفة — <b>وهو ربحٌ كاذبٌ ينتظر أن يُكتشف</b>.
 *
 * <p>
 * ⚠ <b>وصفرٌ ليس «رخيصًا» بل «لم تُسجَّل».</b> صنفٌ كلفتُه صفرٌ يُباع فيظهر ربحُه
 * كاملًا في التقرير، <b>وأرضيّةُ السعر تُلغى</b> — فلا شيءَ يمنع بيعَه بأقلّ
 * ممّا دُفع فيه، لأنّ النظامَ لا يعرف كم دُفع.
 * </p>
 *
 * <p>
 * والجردُ الأوّلُ يُنتج هذه الحالةَ بكثرة: الكلفةُ حقلٌ اختياريٌّ في الإدخال
 * السريع، ومن يُدخل مئةَ صنفٍ في يومٍ يتركها. <b>ولا تظهر في أيّ شاشةٍ إلا
 * إن سُئل عنها</b> — فهذه هي الشاشة.
 * </p>
 */
export default function Uncosted() {
  const { can } = useAuth()
  const [fixing, setFixing] = useState<string | null>(null)

  const rows = useQuery({
    queryKey: ['uncosted'],
    queryFn: () => api<UncostedItem[]>('/api/v1/inventory/uncosted?limit=200'),
    enabled: can(Perm.costView) && can(Perm.movementView),
  })

  if (!can(Perm.costView)) {
    return (
      <Panel title="أصنافٌ بلا كلفة">
        <Banner tone="warn">
          هذه الشاشةُ تعرض كلفةَ البضاعة، وتحتاج صلاحيّةَ «رؤيةُ الكلفة».
        </Banner>
      </Panel>
    )
  }

  const canFix = can(Perm.adjustmentPost)
  const list = rows.data ?? []

  return (
    <Panel title="أصنافٌ بلا كلفة">
      <Banner tone="info">
        <b>رصيدٌ على الرفّ وقيمتُه صفر.</b> هذه الأصنافُ تُباع بربحٍ كاذبٍ في
        التقارير، <b>ولا أرضيّةَ سعرٍ تحميها</b> — فالنظامُ لا يعرف كم دُفع فيها.
      </Banner>

      <ErrorBanner error={rows.error} />

      {rows.isPending ? (
        <div className="mt-4"><Spinner label="يُقرأ الدفتر…" /></div>
      ) : list.length === 0 ? (
        <div className="mt-4">
          <Empty>لا صنفَ بلا كلفة — كلُّ ما على الرفّ له قيمةٌ مُسجَّلة.</Empty>
        </div>
      ) : (
        <>
          <p className="mt-4 text-sm text-ink-soft">
            {list.length} صنفًا ينتظر تصحيحَ كلفته.
            {!canFix && ' والتصحيحُ يحتاج صلاحيّةَ ترحيلِ التسويات.'}
          </p>

          <ul className="mt-2 grid gap-2">
            {list.map((item) => {
              const key = `${item.variantId}:${item.warehouseId}`

              return (
                <li key={key} className="rounded-md border border-line px-3 py-2">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium">{item.productName}</span>
                      <span className="block text-xs text-ink-soft">
                        <span className="tabular" dir="ltr">{item.productCode}</span>
                        {` · ${item.warehouseName}`}
                      </span>
                    </span>

                    <span className="text-sm">
                      <span className="text-ink-soft">الرصيد </span>
                      <b className="tabular" dir="ltr">{formatQty(item.onHand)}</b>
                    </span>

                    <span className="text-sm text-warn">
                      القيمة {formatMoney(item.value)}
                    </span>

                    {canFix && (
                      <Button onClick={() => setFixing(fixing === key ? null : key)}>
                        {fixing === key ? 'أغلِق' : 'صحّح'}
                      </Button>
                    )}

                    <Link
                      className="text-xs text-brand hover:underline"
                      to={`/products/${item.productId}`}
                    >
                      البطاقة
                    </Link>
                  </div>

                  {fixing === key && canFix && (
                    <div className="mt-3 border-t border-line pt-3">
                      <Fixer
                        item={item}
                        onDone={() => {
                          setFixing(null)
                          void rows.refetch()
                        }}
                      />
                    </div>
                  )}
                </li>
              )
            })}
          </ul>
        </>
      )}
    </Panel>
  )
}

/** تصحيحٌ في مكانه — فلا يُفتح لكلِّ صنفٍ صفحةٌ ثمّ يُعاد. */
function Fixer({ item, onDone }: { item: UncostedItem; onDone: () => void }) {
  const [cost, setCost] = useState('')
  const [reason, setReason] = useState('كلفةٌ لم تُدخَل في الجرد الأوّل')

  const fix = useMutation({
    mutationFn: () => api<Revaluation>('/api/v1/inventory/revalue', {
      method: 'POST',
      body: {
        variantId: item.variantId,
        warehouseId: item.warehouseId,
        unitCost: toNumber(cost),
        reason: reason.trim(),
      },
    }),
    onSuccess: onDone,
  })

  const value = toNumber(cost)
  const ready = value !== null && value > 0 && reason.trim().length >= 10 && !fix.isPending

  return (
    <div className="grid gap-2 sm:grid-cols-2">
      <Field label="كلفةُ الوحدة">
        <NumberInput value={cost} onChange={setCost} disabled={fix.isPending} />
      </Field>

      <Field label="السبب" hint="عشرةُ محارفَ فأكثر — يبقى في دفتر الصنف">
        <TextInput value={reason} onChange={setReason} disabled={fix.isPending} />
      </Field>

      <div className="sm:col-span-2">
        {value !== null && value > 0 && (
          <p className="mb-2 text-xs text-ink-soft">
            قيمةُ المخزون تصير{' '}
            <b className="tabular" dir="ltr">
              {formatMoney(Math.round(value * item.onHand * 1000) / 1000)}
            </b>
            {' — ويُكتب مستندُ تصحيحٍ في دفتر الصنف.'}
          </p>
        )}

        <ErrorBanner error={fix.error} />

        <Button tone="primary" disabled={!ready} onClick={() => fix.mutate()}>
          {fix.isPending ? 'يُكتب…' : 'اكتب مستندَ التصحيح'}
        </Button>
      </div>
    </div>
  )
}
